import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiMail, FiX } from 'react-icons/fi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';

const DATE_OPTIONS = [
  { label: 'This Week', value: 'thisWeek' },
  { label: 'Last Week', value: 'lastWeek' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'This Year', value: 'thisYear' },
  { label: 'Custom Dates', value: 'custom' },
];

const toDateOnly = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toNumber = (value, fallback = NaN) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const getRangeBounds = (range, fromDate, toDate) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = today.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() + mondayOffset);

  switch (range) {
    case 'thisWeek': {
      const end = new Date(startOfWeek);
      end.setDate(startOfWeek.getDate() + 6);
      return { start: startOfWeek, end };
    }
    case 'lastWeek': {
      const start = new Date(startOfWeek);
      start.setDate(startOfWeek.getDate() - 7);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    }
    case 'thisMonth':
      return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    case 'lastMonth':
      return {
        start: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        end: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    case 'thisYear':
      return {
        start: new Date(today.getFullYear(), 0, 1),
        end: new Date(today.getFullYear(), 11, 31),
      };
    case 'custom':
      return { start: toDateOnly(fromDate), end: toDateOnly(toDate) };
    default:
      return { start: null, end: null };
  }
};

const isWithinRange = (dateValue, range, fromDate, toDate) => {
  const target = toDateOnly(dateValue);
  // Some summary APIs don't return row-level dates; keep those rows visible.
  if (!target) return true;
  const { start, end } = getRangeBounds(range, fromDate, toDate);
  if (!start || !end) return true;
  return target >= start && target <= end;
};

/** Aligns with EventFeedback / team forms: compare labels ignoring case, spaces, slashes, hyphens. */
const normalizeJobTypeKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\s/_-]/g, '');

const resolveFeedbackEntryRole = (entry) => {
  if (!entry || typeof entry !== 'object') return '';
  const raw = entry.role ?? entry.jobType;
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string' || typeof raw === 'number') return String(raw).trim();
  if (typeof raw === 'object') {
    const label = raw.name ?? raw.jobType ?? raw.title ?? raw.label ?? '';
    return String(label || '').trim();
  }
  return '';
};

const normalizeJobTypes = (response) => {
  const raw = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  return raw
    .map((item) => {
      const id = String(item?._id || item?.id || item?.value || item?.name || item?.jobType || '').trim();
      const name = String(item?.name || item?.jobType || item?.title || item?.label || '').trim();
      if (!name) return null;
      return { id, name };
    })
    .filter(Boolean);
};

const collectRows = (payload) => {
  const data = payload?.data?.data || payload?.data || payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.feedbacks)) return data.feedbacks;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const getEntryAverage = (entry) => {
  const total = toNumber(entry?.total);
  if (Number.isFinite(total)) return total;
  const scores = Array.isArray(entry?.scores) ? entry.scores : [];
  const nums = scores
    .map((item) => toNumber(item?.score))
    .filter((num) => Number.isFinite(num));
  if (!nums.length) return NaN;
  return nums.reduce((sum, value) => sum + value, 0) / nums.length;
};

const normalizeMatchFeedbackRows = (records = [], feedbackType = 'tl') => {
  const employeeMap = new Map();

  records.forEach((record) => {
    const feedbackDate =
      record?.matchId?.startDate ||
      record?.startDate ||
      record?.submittedAt ||
      record?.createdAt ||
      record?.updatedAt;
    const tlEntries = Array.isArray(record?.feedback) ? record.feedback : [];
    const cvEntries = Array.isArray(record?.supervisorFeedback?.ratings)
      ? record.supervisorFeedback.ratings
      : Array.isArray(record?.ratings)
      ? record.ratings
      : [];
    const sourceEntries = feedbackType === 'tl' ? tlEntries : cvEntries;

    sourceEntries.forEach((entry) => {
      const score =
        feedbackType === 'tl' ? getEntryAverage(entry) : toNumber(entry?.rating);
      if (!Number.isFinite(score)) return;

      const employeeId = String(
        entry?.employeeId?._id || entry?.employeeId?.id || entry?.employeeId || entry?.name || ''
      ).trim();
      const employeeName = String(
        entry?.employeeId?.name || entry?.employeeId?.fullName || entry?.name || ''
      ).trim();
      if (!employeeName) return;

      const key = employeeId || employeeName;
      const prev = employeeMap.get(key) || {
        employeeId: key,
        employeeName,
        jobType: resolveFeedbackEntryRole(entry) || '-',
        total: 0,
        count: 0,
        latestDate: feedbackDate || null,
      };

      prev.total += score;
      prev.count += 1;
      if (feedbackDate) prev.latestDate = feedbackDate;
      employeeMap.set(key, prev);
    });
  });

  return Array.from(employeeMap.values()).map((row) => ({
    employeeId: row.employeeId,
    employeeName: row.employeeName,
    jobType: row.jobType,
    score: row.count > 0 ? row.total / row.count : 0,
    date: row.latestDate,
  }));
};

const adminId = (a) => String(a?._id || a?.id || '').trim();

const buildPerformanceReportPdfBase64 = (groupedRows, summaryLines) => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const marginX = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  let cursorY = 14;

  doc.setFontSize(16);
  doc.setTextColor(17, 24, 39);
  doc.text('Performance Report', pageWidth / 2, cursorY, { align: 'center' });
  cursorY += 9;

  doc.setFontSize(9);
  doc.setTextColor(71, 84, 103);
  summaryLines.forEach((line) => {
    doc.text(line, marginX, cursorY);
    cursorY += 4.5;
  });
  doc.setTextColor(0, 0, 0);
  cursorY += 4;

  groupedRows.forEach((group) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(String(group.name), marginX, cursorY);
    doc.setFont('helvetica', 'normal');
    cursorY += 1;

    autoTable(doc, {
      startY: cursorY,
      head: [['Rank', 'Employee', 'Score']],
      body: group.rows.map((row, index) => [
        index + 1,
        row.employeeName,
        Number(row.score || 0).toFixed(1),
      ]),
      styles: { fontSize: 9, cellPadding: 2.5, valign: 'middle' },
      headStyles: { fillColor: [102, 126, 234], textColor: 255, halign: 'center' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 22 },
        1: { halign: 'left' },
        2: { halign: 'right', cellWidth: 28 },
      },
      margin: { left: marginX, right: marginX },
      theme: 'grid',
    });

    cursorY = (doc.lastAutoTable?.finalY ?? cursorY) + 10;
  });

  const dataUri = doc.output('datauristring');
  return dataUri.includes(',') ? dataUri.split(',')[1] : '';
};

const PerformanceReportPage = () => {
  const { jobTypesResponse } = useReferenceData();
  const jobTypes = useMemo(
    () => normalizeJobTypes(jobTypesResponse || {}),
    [jobTypesResponse]
  );

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [adminSearch, setAdminSearch] = useState('');
  const [selectedAdminIds, setSelectedAdminIds] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [dateRange, setDateRange] = useState('thisMonth');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [jobType, setJobType] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [employeeCount, setEmployeeCount] = useState('1');
  const [feedbackType, setFeedbackType] = useState('tl');

  const handleEmployeeCountChange = (e) => {
    const v = e.target.value;
    if (v === '') {
      setEmployeeCount('');
      return;
    }
    const n = Number(v);
    if (!Number.isFinite(n) || n < 1) {
      setEmployeeCount('1');
      return;
    }
    setEmployeeCount(v);
  };

  const handleEmployeeCountBlur = () => {
    const n = toNumber(employeeCount, NaN);
    if (!Number.isFinite(n) || employeeCount === '' || n < 1) {
      setEmployeeCount('1');
    }
  };

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const response = await api.getUsers({ role: 'admin' });
        if (response.success) {
          setAdmins(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Failed to load admins:', error);
      }
    };
    loadAdmins();
  }, []);

  const reportDataFetchKey = useMemo(
    () => [dateRange, fromDate, toDate, feedbackType].join('\u0000'),
    [dateRange, fromDate, toDate, feedbackType]
  );

  useEffect(() => {
    const loadData = async () => {
      if (dateRange === 'custom' && (!fromDate || !toDate)) {
        setRecords([]);
        return;
      }

      setLoading(true);
      try {
        const response = await api.getMatchFeedbacks();
        const rows = normalizeMatchFeedbackRows(collectRows(response), feedbackType);
        setRecords(rows);
      } catch (error) {
        toast.error(error?.message || 'Failed to load performance report');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // Only refetch when date range or feedback source changes; other filters are client-side.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportDataFetchKey]);

  const filteredRows = useMemo(() => {
    let result = records.filter((row) => isWithinRange(row.date, dateRange, fromDate, toDate));
    const count = Math.max(1, toNumber(employeeCount, 1));

    if (jobType !== 'all') {
      const selectedKey = normalizeJobTypeKey(jobType);
      result = result
        .filter((row) => normalizeJobTypeKey(row.jobType) === selectedKey)
        .sort((a, b) => b.score - a.score);

      if (positionFilter === 'top') return result.slice(0, count);
      if (positionFilter === 'bottom') return result.slice(-count).reverse();
      return result;
    }

    if (positionFilter === 'all') {
      return [...result].sort((a, b) => b.score - a.score);
    }

    // For "All" job types + Top/Bottom: take N employees per job type.
    const groupMap = new Map();
    result.forEach((row) => {
      const key = String(row.jobType || '-').trim() || '-';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(row);
    });

    const groupedSelection = [];
    groupMap.forEach((rows) => {
      const sorted = [...rows].sort((a, b) => b.score - a.score);
      if (positionFilter === 'top') {
        groupedSelection.push(...sorted.slice(0, count));
      } else {
        groupedSelection.push(...sorted.slice(-count).reverse());
      }
    });

    return groupedSelection.sort((a, b) => {
      const jobTypeCompare = String(a.jobType || '').localeCompare(String(b.jobType || ''));
      if (jobTypeCompare !== 0) return jobTypeCompare;
      return b.score - a.score;
    });
  }, [records, dateRange, fromDate, toDate, jobType, positionFilter, employeeCount]);

  /** One card per job type: Rank / Employee / Score (same layout for All job types or a single selected type). */
  const groupedRows = useMemo(() => {
    if (!filteredRows.length) return [];

    const map = new Map();
    filteredRows.forEach((row) => {
      const key = String(row.jobType || '-').trim() || '-';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });

    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, rows]) => ({
        name,
        rows: [...rows].sort((a, b) => b.score - a.score),
      }));
  }, [filteredRows]);

  const pdfSummaryLines = useMemo(() => {
    const ftLabel = feedbackType === 'sv' ? 'SV Feedback' : 'TL Feedback';
    const drLabel = DATE_OPTIONS.find((o) => o.value === dateRange)?.label || dateRange;
    const lines = [
      `Feedback type: ${ftLabel}`,
      `Date: ${drLabel}`,
      `Job type: ${jobType === 'all' ? 'All' : jobType}`,
      `Selection: ${positionFilter === 'all' ? 'All' : positionFilter === 'top' ? 'Top' : 'Bottom'}`,
    ];
    if (dateRange === 'custom' && fromDate && toDate) {
      lines.push(`Range: ${fromDate} to ${toDate}`);
    }
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    return lines;
  }, [dateRange, fromDate, toDate, jobType, positionFilter, feedbackType]);

  const emailFiltersPayload = useMemo(
    () => ({
      dateRange,
      fromDate,
      toDate,
      jobType,
      positionFilter,
      employeeCount,
      feedbackType,
    }),
    [dateRange, fromDate, toDate, jobType, positionFilter, employeeCount, feedbackType]
  );

  const filteredAdmins = useMemo(() => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return admins;
    return admins.filter((a) => {
      const name = String(a.name || a.fullName || '').toLowerCase();
      const email = String(a.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [admins, adminSearch]);

  const toggleAdminSelected = useCallback((id) => {
    const sid = String(id);
    setSelectedAdminIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    );
  }, []);

  const selectAllFilteredAdmins = useCallback(() => {
    const withEmail = filteredAdmins.filter((a) => String(a.email || '').trim());
    const ids = withEmail.map((a) => adminId(a)).filter(Boolean);
    setSelectedAdminIds((prev) => Array.from(new Set([...prev, ...ids])));
  }, [filteredAdmins]);

  const clearAdminSelection = useCallback(() => setSelectedAdminIds([]), []);

  const handleSendPerformancePdfEmail = async () => {
    if (!groupedRows.length) {
      toast.error('No report data to send.');
      return;
    }
    const recipients = selectedAdminIds
      .map((id) => admins.find((a) => adminId(a) === id))
      .filter(Boolean)
      .map((a) => String(a.email || '').trim())
      .filter(Boolean);
    const uniqueEmails = [...new Set(recipients)];
    if (!uniqueEmails.length) {
      toast.error('Select at least one admin with an email address.');
      return;
    }

    try {
      setSendingEmail(true);
      const pdfBase64 = buildPerformanceReportPdfBase64(groupedRows, pdfSummaryLines);
      if (!pdfBase64) {
        throw new Error('Failed to build PDF.');
      }
      const safeDate = new Date().toISOString().slice(0, 10);
      const fileName = `performance-report-${safeDate}.pdf`;

      await api.sendPerformanceReportPdfEmail({
        recipientEmails: uniqueEmails,
        pdfBase64,
        fileName,
        subject: 'Performance Report',
        filters: emailFiltersPayload,
      });

      toast.success(`Report queued for ${uniqueEmails.length} recipient(s).`);
      setShowEmailModal(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to send email.');
    } finally {
      setSendingEmail(false);
    }
  };

  const filterLabelStyle = { fontSize: '12px', color: '#667085', fontWeight: 500, lineHeight: 1.2 };
  const filterControlStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d0d5dd',
    borderRadius: '7px',
    fontSize: '12px',
    background: '#fff',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '16px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'flex-end',
            width: '100%',
            gap: '12px',
            minWidth: 0,
            marginBottom: '14px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'flex-end',
              gap: '12px',
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: '100%',
              overflowX: 'auto',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 150px', minWidth: '120px' }}>
              <label htmlFor="perf-filter-date" style={filterLabelStyle}>
                Date
              </label>
              <select
                id="perf-filter-date"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                style={filterControlStyle}
              >
                {DATE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 140px', minWidth: '110px' }}>
              <label htmlFor="perf-filter-job-type" style={filterLabelStyle}>
                Job Type
              </label>
              <select
                id="perf-filter-job-type"
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                style={filterControlStyle}
              >
                <option value="all">All</option>
                {jobTypes.map((item) => (
                  <option key={item.id || item.name} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 120px', minWidth: '100px' }}>
              <label htmlFor="perf-filter-position" style={filterLabelStyle}>
                Select
              </label>
              <select
                id="perf-filter-position"
                value={positionFilter}
                onChange={(e) => setPositionFilter(e.target.value)}
                style={filterControlStyle}
              >
                <option value="all">All</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 150px', minWidth: '110px' }}>
              <label htmlFor="perf-filter-feedback-type" style={filterLabelStyle}>
                Feedback Type
              </label>
              <select
                id="perf-filter-feedback-type"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                style={filterControlStyle}
              >
                <option value="tl">TL Feedback</option>
                <option value="sv">SV Feedback</option>
              </select>
            </div>
            {positionFilter !== 'all' && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '5px',
                  flex: '0 1 120px',
                  minWidth: '132px',
                }}
              >
                <label
                  htmlFor="perf-filter-employee-count"
                  style={{ ...filterLabelStyle, whiteSpace: 'nowrap' }}
                >
                  No. of Employees
                </label>
                <input
                  id="perf-filter-employee-count"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="e.g. 5"
                  value={employeeCount}
                  onChange={handleEmployeeCountChange}
                  onBlur={handleEmployeeCountBlur}
                  style={{ ...filterControlStyle, background: 'white' }}
                />
              </div>
            )}
          </div>
          <div
            aria-hidden
            style={{
              flex: '1 1 0',
              minWidth: '16px',
              alignSelf: 'stretch',
            }}
          />
          <button
            type="button"
            onClick={() => setShowEmailModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              flexShrink: 0,
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '7px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <FiMail size={16} strokeWidth={2.5} aria-hidden />
            Email Report
          </button>
        </div>

        {dateRange === 'custom' && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: '12px',
              marginBottom: '14px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 180px', minWidth: '140px' }}>
              <label htmlFor="perf-filter-from-date" style={filterLabelStyle}>
                From Date
              </label>
              <input
                id="perf-filter-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={{ ...filterControlStyle }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: '0 1 180px', minWidth: '140px' }}>
              <label htmlFor="perf-filter-to-date" style={filterLabelStyle}>
                To Date
              </label>
              <input
                id="perf-filter-to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={{ ...filterControlStyle }}
              />
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ border: '1px solid #eaecf0', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ padding: '18px', textAlign: 'center', fontSize: '13px', color: '#667085' }}>Loading...</div>
          </div>
        ) : groupedRows.length === 0 ? (
          <div style={{ border: '1px solid #eaecf0', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ padding: '18px', textAlign: 'center', fontSize: '13px', color: '#667085' }}>
              No performance data found for selected filters.
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '16px',
              alignItems: 'start',
            }}
          >
            {groupedRows.map((group) => (
              <div
                key={group.name}
                style={{
                  minWidth: 0,
                  border: '1px solid #eaecf0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  background: '#fff',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                }}
              >
                <div
                  style={{
                    padding: '10px 12px',
                    fontSize: '13px',
                    fontWeight: 700,
                    color: '#ffffff',
                    background: 'linear-gradient(180deg, #1a202c 0%, #2d3748 100%)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  {group.name}
                </div>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    background: '#ffffff',
                  }}
                >
                  <thead style={{ background: '#e4e7ec' }}>
                    <tr style={{ color: '#344054', fontSize: '11px', fontWeight: 600 }}>
                      <th
                        style={{
                          padding: '8px 10px',
                          fontSize: '11px',
                          textAlign: 'center',
                          borderBottom: '1px solid #d0d5dd',
                          width: '22%',
                        }}
                      >
                        Rank
                      </th>
                      <th
                        style={{
                          padding: '8px 10px',
                          fontSize: '11px',
                          textAlign: 'left',
                          borderBottom: '1px solid #d0d5dd',
                          width: '50%',
                        }}
                      >
                        Employee
                      </th>
                      <th
                        style={{
                          padding: '8px 10px',
                          fontSize: '11px',
                          textAlign: 'right',
                          borderBottom: '1px solid #d0d5dd',
                          width: '28%',
                        }}
                      >
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row, index) => (
                      <tr
                        key={`${group.name}-${row.employeeId}-${index}`}
                        style={{
                          borderTop: '1px solid #eaecf0',
                          background: '#ffffff',
                        }}
                      >
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontSize: '12px', color: '#475467' }}>
                          {index + 1}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            fontSize: '12px',
                            color: '#111827',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.employeeName}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: '12px', color: '#111827', fontWeight: 700 }}>
                          {Number(row.score || 0).toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {showEmailModal && (
        <div
          onClick={() => setShowEmailModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(620px, 100%)',
              maxHeight: '76vh',
              overflowY: 'auto',
              background: 'white',
              borderRadius: '10px',
              border: '1px solid #eaecf0',
              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.18)',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>Email Report to Admins</div>
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                aria-label="Close"
                style={{
                  border: '1px solid #e8ecf0',
                  background: '#f9fafb',
                  color: '#64748b',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <FiX size={17} strokeWidth={2} />
              </button>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="search"
                placeholder="Search admins by name or email..."
                value={adminSearch}
                onChange={(e) => setAdminSearch(e.target.value)}
                style={{
                  flex: '1 1 220px',
                  minWidth: '180px',
                  padding: '7px 9px',
                  border: '1px solid #d0d5dd',
                  borderRadius: '7px',
                  fontSize: '12px',
                }}
              />
              <button
                type="button"
                onClick={selectAllFilteredAdmins}
                style={{
                  padding: '7px 10px',
                  fontSize: '12px',
                  borderRadius: '7px',
                  border: '1px solid #d0d5dd',
                  background: 'white',
                  cursor: 'pointer',
                  color: '#344054',
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={clearAdminSelection}
                style={{
                  padding: '7px 10px',
                  fontSize: '12px',
                  borderRadius: '7px',
                  border: '1px solid #d0d5dd',
                  background: 'white',
                  cursor: 'pointer',
                  color: '#344054',
                }}
              >
                Clear
              </button>
              <button
                type="button"
                onClick={handleSendPerformancePdfEmail}
                disabled={sendingEmail || loading}
                style={{
                  padding: '7px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  borderRadius: '7px',
                  border: 'none',
                  background:
                    sendingEmail || loading
                      ? '#cbd5e1'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  cursor: sendingEmail || loading ? 'not-allowed' : 'pointer',
                }}
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>

            <div
              style={{
                maxHeight: '36vh',
                overflowY: 'auto',
                border: '1px solid #eaecf0',
                borderRadius: '7px',
                background: 'white',
              }}
            >
              {filteredAdmins.length === 0 ? (
                <div style={{ padding: '12px', fontSize: '12px', color: '#667085' }}>
                  {admins.length === 0 ? 'No admins loaded.' : 'No admins match your search.'}
                </div>
              ) : (
                filteredAdmins.map((a) => {
                  const id = adminId(a);
                  if (!id) return null;
                  const email = String(a.email || '').trim();
                  const disabled = !email;
                  const checked = selectedAdminIds.includes(id);
                  return (
                    <label
                      key={id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '8px 12px',
                        borderBottom: '1px solid #f2f4f7',
                        fontSize: '13px',
                        color: disabled ? '#98a2b3' : '#344054',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleAdminSelected(id)}
                      />
                      <span style={{ fontWeight: 600, color: '#111827' }}>{a.name || a.fullName || 'Admin'}</span>
                      <span style={{ fontSize: '12px', color: '#667085' }}>{email || 'No email'}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceReportPage;
